import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { ShieldCheck, Camera, CheckCircle, AlertTriangle, Lock, Eye, FileText, Upload, X } from 'lucide-react';

const TIPS = [
  { icon: Lock, title: 'Mot de passe fort', desc: 'Utilisez au moins 8 caracteres avec majuscules, chiffres et symboles.', done: true },
  { icon: Camera, title: 'Photo verifiee', desc: 'Prenez un selfie pour verifier votre identite et obtenir le badge.', done: false },
  { icon: Eye, title: 'Profil visible', desc: 'Votre profil est visible par les autres utilisateurs.', done: true },
  { icon: FileText, title: 'Bio complete', desc: 'Les profils avec une bio recoivent 3x plus de matchs.', done: false },
];

export default function Security() {
  const { user } = useAuth();
  const [verifyStep, setVerifyStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);

  const securityScore = TIPS.filter(t => t.done).length / TIPS.length * 100;

  const handleVerifyPhoto = () => {
    setVerifyStep(1);
    setTimeout(() => setVerifyStep(2), 2000);
  };

  const loadBlocked = async () => {
    try {
      const res = await api.get('/users/blocked');
      setBlockedUsers(res.data || []);
    } catch (e) { setBlockedUsers([]); }
    setShowBlockedModal(true);
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="security-page">
      <header className="px-8 py-6" style={{borderBottom:'1px solid var(--border-color)'}}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={24} className="text-green-400" />
          <div>
            <h1 className="text-2xl font-black" style={{color:'var(--text-primary)'}}>Centre de Securite</h1>
            <p className="text-sm mt-0.5" style={{color:'var(--text-muted)'}}>Protegez votre compte — Exclusif Web</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Security Score */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold" style={{color:'var(--text-primary)'}}>Score de securite</h3>
            <span className={`text-2xl font-black ${securityScore >= 75 ? 'text-green-400' : securityScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{Math.round(securityScore)}%</span>
          </div>
          <div className="w-full h-3 rounded-full" style={{background:'var(--bg-input)'}}>
            <div className={`h-full rounded-full transition-all ${securityScore >= 75 ? 'bg-gradient-to-r from-green-500 to-emerald-400' : securityScore >= 50 ? 'bg-gradient-to-r from-amber-500 to-yellow-400' : 'bg-gradient-to-r from-red-500 to-rose-400'}`} style={{width:`${securityScore}%`}} />
          </div>
          <p className="text-xs mt-3" style={{color:'var(--text-muted)'}}>Completez les etapes ci-dessous pour ameliorer votre securite</p>
        </div>

        {/* Checklist */}
        <div className="glass-card p-6">
          <h3 className="text-sm font-bold mb-4" style={{color:'var(--text-primary)'}}>Checklist securite</h3>
          <div className="space-y-3">
            {TIPS.map(({ icon: Icon, title, desc, done }, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl transition-colors" style={{background: done ? 'rgba(34,197,94,0.05)' : 'var(--bg-card)'}}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${done ? 'bg-green-500/15' : 'bg-amber-500/15'}`}>
                  {done ? <CheckCircle size={18} className="text-green-400" /> : <AlertTriangle size={18} className="text-amber-400" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{color:'var(--text-primary)'}}>{title}</p>
                  <p className="text-xs mt-0.5" style={{color:'var(--text-muted)'}}>{desc}</p>
                </div>
                {done ? (
                  <span className="text-xs px-3 py-1 rounded-full bg-green-500/15 text-green-400 font-semibold">Fait</span>
                ) : (
                  <span className="text-xs px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 font-semibold">A faire</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Photo Verification */}
        <div className="glass-card p-6">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
            <Camera size={16} className="text-brand-violet" /> Verification photo
          </h3>
          <p className="text-xs mb-4" style={{color:'var(--text-muted)'}}>Prenez un selfie pour prouver que vous etes une vraie personne. Vous obtiendrez un badge de verification visible sur votre profil.</p>

          {verifyStep === 0 && (
            <button onClick={handleVerifyPhoto}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-brand-violet to-brand-purple text-white font-semibold text-sm hover:shadow-lg transition-all"
              data-testid="verify-photo-btn"
            >
              <Upload size={16} /> Lancer la verification
            </button>
          )}
          {verifyStep === 1 && (
            <div className="flex items-center gap-3 p-4 rounded-xl" style={{background:'var(--bg-card)'}}>
              <div className="w-8 h-8 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" />
              <span className="text-sm" style={{color:'var(--text-secondary)'}}>Analyse en cours...</span>
            </div>
          )}
          {verifyStep === 2 && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
              <CheckCircle size={20} className="text-green-400" />
              <span className="text-sm font-semibold text-green-400">Verification soumise ! Vous recevrez le badge sous 24h.</span>
            </div>
          )}
        </div>

        {/* Blocked Users */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold" style={{color:'var(--text-primary)'}}>Utilisateurs bloques</h3>
            <button onClick={loadBlocked} className="text-xs text-brand-violet font-semibold hover:opacity-70" data-testid="view-blocked-btn">Voir la liste</button>
          </div>
          <p className="text-xs" style={{color:'var(--text-muted)'}}>Les utilisateurs bloques ne peuvent plus voir votre profil ni vous contacter.</p>
        </div>

        {/* Safety Tips */}
        <div className="glass-card p-6">
          <h3 className="text-sm font-bold mb-4" style={{color:'var(--text-primary)'}}>Conseils de securite</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Ne partagez jamais vos informations financieres',
              'Faites un appel video avant de rencontrer quelqu\'un',
              'Rencontrez-vous toujours dans un lieu public',
              'Informez un proche de vos rendez-vous',
              'Signalez tout comportement suspect',
              'Ne cliquez pas sur des liens suspects dans les messages',
            ].map((tip) => (
              <div key={tip} className="flex items-start gap-2 p-3 rounded-lg" style={{background:'var(--bg-card)'}}>
                <ShieldCheck size={14} className="text-green-400 shrink-0 mt-0.5" />
                <span className="text-xs" style={{color:'var(--text-secondary)'}}>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Blocked Modal */}
      {showBlockedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowBlockedModal(false)}>
          <div className="glass-card w-full max-w-md mx-4 p-6 relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowBlockedModal(false)} className="absolute top-4 right-4" style={{color:'var(--text-muted)'}}><X size={20} /></button>
            <h2 className="text-lg font-bold mb-4" style={{color:'var(--text-primary)'}}>Utilisateurs bloques</h2>
            {blockedUsers.length === 0 ? (
              <p className="text-sm text-center py-6" style={{color:'var(--text-muted)'}}>Aucun utilisateur bloque</p>
            ) : (
              blockedUsers.map((u) => (
                <div key={u.id || u.email} className="flex items-center justify-between p-3 rounded-xl mb-2" style={{background:'var(--bg-card)'}}>
                  <span className="text-sm" style={{color:'var(--text-primary)'}}>{u.name || u.email}</span>
                  <button className="text-xs text-red-400 font-semibold">Debloquer</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
